'use client'

import * as React from 'react'
import { cn } from '../../lib/utils'
import type { LucideIcon } from 'lucide-react'

/**
 * Predefined color variants for StatsWidget
 * Using static classes ensures Tailwind doesn't purge them
 */
const colorVariants: Record<string, string> = {
  // Status colors
  emerald: 'bg-gradient-to-br from-emerald-500 to-teal-500',
  green: 'bg-gradient-to-br from-emerald-500 to-teal-500',
  amber: 'bg-gradient-to-br from-amber-500 to-orange-500',
  orange: 'bg-gradient-to-br from-amber-500 to-orange-500',
  blue: 'bg-gradient-to-br from-blue-500 to-cyan-500',
  cyan: 'bg-gradient-to-br from-blue-500 to-cyan-500',
  slate: 'bg-gradient-to-br from-slate-700 to-slate-800',
  gray: 'bg-gradient-to-br from-slate-700 to-slate-800',
  // Additional colors
  red: 'bg-gradient-to-br from-red-500 to-rose-500',
  purple: 'bg-gradient-to-br from-purple-500 to-violet-500',
  violet: 'bg-gradient-to-br from-violet-500 to-purple-500',
  pink: 'bg-gradient-to-br from-pink-500 to-rose-500',
  indigo: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
  teal: 'bg-gradient-to-br from-teal-500 to-cyan-500',
  // Primary
  primary: 'bg-gradient-to-br from-primary to-primary/80',
}

type ColorVariant = keyof typeof colorVariants

interface StatsWidgetProps {
  /** Label for the stat */
  label: string
  /** Value to display */
  value: string | number
  /** Icon component from lucide-react */
  icon: LucideIcon
  /** Color variant for the icon background (defaults to 'slate') */
  color?: ColorVariant
  /** Alias for color - use either color or variant */
  variant?: ColorVariant
  /** Optional className for custom styling */
  className?: string
  /** Optional onClick handler */
  onClick?: () => void
  /** Optional badge content */
  badge?: React.ReactNode
}

interface StatsWidgetGridProps {
  children: React.ReactNode
  /** Number of columns - always this many on desktop, responsive on smaller screens */
  columns?: 2 | 3 | 4
  className?: string
}

function StatsWidget({
  label,
  value,
  icon: Icon,
  color,
  variant,
  className,
  onClick,
  badge,
}: StatsWidgetProps) {
  const Comp = onClick ? 'button' : 'div'
  const chosenColor = (variant ?? color) as ColorVariant
  const gradientClass = colorVariants[chosenColor] || colorVariants.slate

  return (
    <Comp
      className={cn(
        'glass-card min-w-0 rounded-2xl p-4 lg:p-5 flex items-center gap-3 lg:gap-4 transition-all text-start',
        onClick && 'cursor-pointer active:scale-[0.98] md:hover:scale-[1.02] md:hover:shadow-lg',
        className
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          'w-11 h-11 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0 relative overflow-hidden',
          gradientClass
        )}
        style={
          chosenColor === 'slate' || chosenColor === 'gray'
            ? {
                background: 'linear-gradient(to bottom right, rgb(51 65 85), rgb(30 41 59))',
              }
            : chosenColor === 'indigo'
            ? {
                background: 'linear-gradient(to bottom right, rgb(99 102 241), rgb(79 70 229))', // indigo-500 to indigo-600
              }
            : chosenColor === 'red'
            ? {
                background: 'linear-gradient(to bottom right, rgb(239 68 68), rgb(220 38 38))', // red-500 to red-700
              }
            : undefined
        }
      >
        <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white relative z-10" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs lg:text-sm font-medium text-muted-foreground leading-snug">
          {label}
        </p>
        <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          <p className="break-words text-lg lg:text-2xl font-bold text-foreground">
            {value}
          </p>
          {badge ? (
            <div className="min-w-0 max-w-full">
              {badge}
            </div>
          ) : null}
        </div>
      </div>
    </Comp>
  )
}

function StatsWidgetGrid({
  children,
  columns = 4,
  className,
}: StatsWidgetGridProps) {
  return (
    <div className={cn(
      'grid gap-3 lg:gap-4',
      columns === 2 && 'grid-cols-2',
      columns === 3 && 'grid-cols-2 sm:grid-cols-3',
      columns === 4 && 'grid-cols-2 sm:grid-cols-4',
      className
    )}>
      {children}
    </div>
  )
}

export { StatsWidget, StatsWidgetGrid, colorVariants, type StatsWidgetProps, type StatsWidgetGridProps, type ColorVariant }
